import { inject, Service, signal } from '@angular/core';
import Dexie, { IndexableType } from 'dexie';
import { DATABASE_CONFIG_TOKEN, LOCAL_STORES, LocalStore } from './config/database-config-token';
import { CandidateQuestionEntity } from './entities/candidate-question-entity';
import { QuestionSectionEntity } from './entities/question-section-entity';
import { FskDexie } from './fsk-dexie';

/**
 * Sends each database call to the local store of the area of the app that is in use.
 *
 * A real exam and a practice paper (mock or sample) each have their own IndexedDB database.
 */
@Service()
export class AppDatabase {
	private readonly configs = inject(DATABASE_CONFIG_TOKEN);
	private readonly databases = new Map<LocalStore, FskDexie>();
	private readonly activeStore = signal<LocalStore>('exam');

	public readonly store = this.activeStore.asReadonly();

	public get candidateQuestions(): Dexie.Table<CandidateQuestionEntity, string> {
		return this.active.candidateQuestions;
	}

	public get questionSections(): Dexie.Table<QuestionSectionEntity, string> {
		return this.active.questionSections;
	}

	public use(store: LocalStore): void {
		this.activeStore.set(store);
	}

	public table<TEntity, TKey extends IndexableType>(name: string): Dexie.Table<TEntity, TKey> {
		return this.active.table<TEntity, TKey>(name);
	}

	/** Deletes the data in every local store, not only in the active one. */
	public async clearAll(): Promise<void> {
		await Promise.all(LOCAL_STORES.map((store: LocalStore) => {
			const database = this.open(store);
			return database.transaction('rw', database.tables, () => Promise.all(database.tables.map(table => table.clear())));
		}));
	}

	private get active(): FskDexie {
		return this.open(this.activeStore());
	}

	private open(store: LocalStore): FskDexie {
		let database = this.databases.get(store);
		if (!database) {
			database = new FskDexie(this.configs[store]);
			this.databases.set(store, database);
		}

		return database;
	}
}
